import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RouteSearch.css';
import FlightMap from './FlightMap';
import axios from 'axios';
import SimbriefButton from './SimBriefButton';
import IvaoButton from './IVAOButton';
/**
 * RouteSearchPage Component
 * Modern aviation-themed route search with professional UI
 */
const RouteSearch = (props) => {

    const navigate = useNavigate();
    const location = useLocation();

    const getUserLocation = () => {
        // Prefer location from persisted userData (set at login), then fall back to props.
        // This ensures the Departure field defaults to the logged-in user's current location.
        try {
            const stored = localStorage.getItem('userData');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.location) return String(parsed.location).toUpperCase();
            }
        } catch (e) {
            // ignore JSON/localStorage errors and fall back to props
        }

        if (props?.userData?.location) return String(props.userData.location).toUpperCase();
        if (props?.location) return String(props.location).toUpperCase();
        return '';
    };
    const getUserCallsign = () => {
        // Prefer location from persisted userData (set at login), then fall back to props.
        try {
            const stored = localStorage.getItem('userData');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.callsign) return String(parsed.callsign).toUpperCase();
            }
        } catch (e) {
            // ignore JSON/localStorage errors and fall back to props
        }

        if (props?.userData?.callsign) return String(props.userData.callsign).toUpperCase();
        return 'FDX???';
    };    
    const aircraftOptions = [
        { value: "A306", label: "Airbus A300-600" },
        { value: "A310", label: "Airbus A310" },
        { value: "AT42", label: "Aerospatial ATR-42" },
        { value: "AT72", label: "Aerospatial ATR-72" },
        { value: "B722", label: "Boeing 727-200" },
        { value: "B732", label: "Boeing 737-200" },
        { value: "B738", label: "Boeing 737-800X" },
        { value: "B752", label: "Boeing 757-200" },
        { value: "B76F", label: "Boeing 767 Freighter" },
        { value: "B77F", label: "Boeing 777 Freighter" },
        { value: "B744", label: "Boeing 747-400" },
        { value: "B748", label: "Boeing 747-800" },
        { value: "C208", label: "Cessna Caravan 208B" },
        { value: "C408", label: "Cessna SkyCourier" },
        { value: "DC87", label: "Douglas DC-8" },
        { value: "MD10", label: "McDonnell Douglas DC-10" },
        { value: "MD11", label: "McDonnell Douglas MD-11" },
        { value: "FA20", label: "Dassault Falcon 20" },
    ];

    // Stage 0: Initial Form, Stage 1: Results Table, Stage 2: Flight Details
    const [stage, setStage] = useState(0);
    const [orig, setOrig] = useState(getUserLocation);
    const [dest, setDest] = useState('');
    const [dist, setDist] = useState('0,9999');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routeDetails, setRouteDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPilotLocation, setCurrentPilotLocation] = useState(getUserLocation);
    const [showLoadsheetModal, setShowLoadsheetModal] = useState(false);
    const [selectedAircraft, setSelectedAircraft] = useState(() => localStorage.getItem('selectedAircraft') || 'A306');
    const [useKG, setUseKG] = useState(() => localStorage.getItem('useKG') === 'true');
    const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [editPlan, setEditPlan] = useState('');
    const [hideFerryRoutes, setHideFerryRoutes] = useState(false);
    const [callsign, setCallsign] = useState(''); 

    //const [useJumpseat, setUseJumpseat] = useState(false); 


    useEffect(() => {
        const loc = getUserLocation();
        setOrig(loc);
        setCurrentPilotLocation(loc);
        const cs = getUserCallsign();
        setCallsign(cs);
        return () => {}
    }, [props.location]);
    
    const distanceOptions = [
        { value: "0,9999", label: "All" },
        { value: "0,400", label: "<400 NM" },
        { value: "182,591", label: "1 Hour" },
        { value: "591,1000", label: "2 Hours" },
        { value: "1000,1409", label: "3 Hours" },
        { value: "1409,1818", label: "4 Hours" },
        { value: "1818,2227", label: "5 Hours" },
        { value: "2227,2636", label: "6 Hours" },
        { value: "2636,3045", label: "7 Hours" },
        { value: "3045,3454", label: "8 Hours" },
        { value: "3454,3863", label: "9 Hours" },
        { value: "3863,4272", label: "10 Hours" },
        { value: "4272,5090", label: "11 Hours" },
        { value: "5090,9999", label: "12+ Hours" },
    ];


    const fetchRoutes = async (obj) => {
        setLoading(true);
        setError(null);
        setStage(1);
        setIsSearchCollapsed(true); // Collapse search when results are displayed
        try {
            // const request = "https://fdxva.org/api/routes/route_search.php?orig=" + obj.orig + "&dest=" + obj.dest + "&r=" + Math.random();
            const result = await axios.get("/api/routes/search", {
                params: { _t: Date.now(),  orig: obj.orig, dest: obj.dest, dist: obj.dist }
            });                    

            const resObj = result.data;
            console.log(result.data)
            console.log(result.data.routes)

            if(resObj.success === true){
                console.log("success")
                //localStorage.setItem("routeSearch", JSON.stringify(resObj.route_data.results));
                setSearchResults(resObj.routes);
            } else {
                console.log("failed")
                setError('Failed to fetch search');
                setSearchResults([]);
            }
        } catch (e) {
            setError('Network error while searching for routes.');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchRouteDetails = async (obj) => {
        setLoading(true);
        setError(null);
        setSelectedRoute(obj);
        setRouteDetails(null);
        setStage(2);

        try {
            const result = await axios.get("/api/routes/detail", {
                params: { _t: Date.now(),  orig: obj.orig, dest: obj.dest }
            });                                
            const resObj = result.data;
             
            if(resObj.success === true){
                console.log(result.data);
                setRouteDetails(result.data);
            } else {
                setError('Failed to fetch detail.');
                setSearchResults([]);
            }
        } catch (e) {
            setError('Network error while fetching route details.');
            console.error(e);
            setStage(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (location.state?.route) {
            const { orig, dest } = location.state.route;
            setOrig(orig);
            setDest(dest);
            fetchRouteDetails({ orig, dest });
        } else if (location.state?.restoreResults) {
            const { orig: stateOrig, dest: stateDest } = location.state;
            if (stateOrig) setOrig(stateOrig);
            if (stateDest) setDest(stateDest);
            fetchRoutes({ orig: stateOrig || '', dest: stateDest || ''});
            setIsSearchCollapsed(false);
        }
    }, [location.state]);

    const handleSearch = (e) => {
        e.preventDefault();
        // Allow search with just origin if destination is empty
        if (!orig && !dest) {
            setError('Please enter at least an origin or destination airport.');
            console.log("to pacify no-unused-vars:" + selectedRoute)
            return;
        }
        fetchRoutes({ orig, dest, dist });
    };

    const handleRouteSelect = (route) => {
        fetchRouteDetails(route); 
    };

    const handleBackToResults = () => {
        setStage(1);
        setRouteDetails(null);
        setSelectedRoute(null);
    };
 
    const handleLoadsheetSubmit = (e) => {
        e.preventDefault();
        
        // Create an object with the form data
        const loadsheetData = {
            orig: routeDetails.route.orig,
            dest: routeDetails.route.dest,
            eqpt: selectedAircraft,
            kg: useKG ? '1' : '0',
            //jumpseat: useJumpseat ? routeDetails.route.orig : ''
        };
        
        // Navigate to the loadsheet page with state containing the form data
        navigate('/loadsheet', { state: { loadsheetData } });
    };

    const updateFlightPlan = async (orig, dest, plan) => {
        try {
            const result = await axios.post('/api/routes/save_plan', { orig, dest, plan });
            if (result.data?.plan !== undefined) {
                setRouteDetails(prev => ({
                    ...prev,
                    route: { ...prev.route, plan: result.data.plan }
                }));
            }
        } catch (e) {
            console.error('Failed to update flight plan:', e);
        }
    };

    const renderSearchForm = () => (
        <div className="rs-container">
            {/* Search Card */}
            <div className="rs-search-card">
                <div className="rs-card-header">
                    <div className="rs-card-icon">🔍</div>
                    <h2 className="rs-card-title">Search Routes</h2>
                </div>
                
                <form onSubmit={handleSearch} className="rs-search-form">
                    <div className="rs-form-row">
                        <div className="rs-form-group">
                            <label htmlFor="orig">
                                <span className="rs-label-icon">🛫</span>
                                Departure
                            </label>
                            <input 
                                id="orig"
                                type="text" 
                                name="ORIG" 
                                value={orig} 
                                onChange={(e) => setOrig(e.target.value.toUpperCase())}
                                placeholder="ICAO"
                                maxLength="4"
                                className="rs-input-field"
                            />
                        </div>

                        <div className="rs-form-group">
                            <label htmlFor="dest">
                                <span className="rs-label-icon">🛬</span>
                                Arrival
                            </label>
                            <input 
                                id="dest"
                                type="text" 
                                name="DEST" 
                                value={dest} 
                                onChange={(e) => setDest(e.target.value.toUpperCase())}
                                placeholder="ICAO"
                                maxLength="4"
                                className="rs-input-field"
                                autoFocus
                            />
                        </div>

                        <div className="rs-form-group">
                            <label htmlFor="dist">
                                <span className="rs-label-icon">⏱️</span>
                                Flight Length
                            </label>
                            <select 
                                id="dist"
                                name="DIST" 
                                value={dist}
                                onChange={(e) => setDist(e.target.value)}
                                className="rs-select-field"
                            >
                                {distanceOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="rs-form-group">
                            <label>&nbsp;</label>
                            <button type="submit" className="rs-btn-primary">
                                <span className="rs-btn-icon">🔍</span>
                                Search Routes
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );

    const renderResultsTable = () => {
        return (
            <div className="rs-container">
                {/* Search Card - Collapsible Version */}
                <div className="rs-search-card rs-compact">
                    <div className="rs-card-header" style={{ cursor: 'pointer' }} onClick={() => setIsSearchCollapsed(!isSearchCollapsed)}>
                        <div className="rs-card-icon">🔍</div>
                        <h2 className="rs-card-title">Search Routes</h2>
                        <button
                            type="button"
                            className="rs-collapse-toggle"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsSearchCollapsed(!isSearchCollapsed);
                            }}
                            aria-label={isSearchCollapsed ? "Expand search" : "Collapse search"}
                        >
                            {isSearchCollapsed ? '▼' : '▲'}
                        </button>
                    </div>
                    
                    {!isSearchCollapsed && (
                        <form onSubmit={handleSearch} className="rs-search-form">
                            <div className="rs-form-row">
                                <div className="rs-form-group">
                                    <label htmlFor="orig-results">From</label>
                                    <input
                                        id="orig-results"
                                        type="text"
                                        value={orig}
                                        onChange={(e) => setOrig(e.target.value.toUpperCase())}
                                        placeholder="ICAO"
                                        maxLength="4"
                                        className="rs-input-field"
                                    />
                                </div>

                                <div className="rs-form-group">
                                    <label htmlFor="dest-results">To</label>
                                    <input
                                        id="dest-results"
                                        type="text"
                                        value={dest}
                                        onChange={(e) => setDest(e.target.value.toUpperCase())}
                                        placeholder="ICAO"
                                        maxLength="4"
                                        className="rs-input-field"
                                    />
                                </div>

                                <div className="rs-form-group">
                                    <label htmlFor="dist-results">Filter (optional)</label>
                                    <select
                                        id="dist-results"
                                        value={dist}
                                        onChange={(e) => setDist(e.target.value)}
                                        className="rs-select-field"
                                    >
                                        {distanceOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="rs-form-group">
                                    <label>&nbsp;</label>
                                    <button type="submit" className="rs-btn-primary">Search</button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* Jumpseat Notice */}
                {orig && currentPilotLocation && orig !== currentPilotLocation && (
                    <div className="rs-jumpseat-notice">
                        <span className="rs-jumpseat-notice-icon">⚠️</span>
                        <div className="rs-jumpseat-notice-body">
                            <span className="rs-jumpseat-notice-title">Jumpseat Required</span>
                            <p className="rs-jumpseat-notice-text">
                                The departure airport <strong>{orig}</strong> does not match your current location (<strong>{currentPilotLocation}</strong>). You will need to arrange a jumpseat to <strong>{orig}</strong> before operating this flight.
                            </p>
                        </div>
                    </div>
                )}

                {/* Results Card */}
                {(() => {
                    const hasFerryRoutes = searchResults.some(r => r.type?.toUpperCase() === 'FERRY');
                    const filteredResults = hideFerryRoutes
                        ? searchResults.filter(r => r.type?.toUpperCase() !== 'FERRY')
                        : searchResults;
                    return (
                <div className="rs-results-card">
                    <div className="rs-card-header">
                        <div className="rs-card-icon">📋</div>
                        <h2 className="rs-card-title">
                            Available Routes
                            <span className="rs-results-count">{filteredResults.length} flights found</span>
                        </h2>
                        {hasFerryRoutes && (
                            <label className="rs-ferry-filter-label">
                                <input
                                    type="checkbox"
                                    checked={hideFerryRoutes}
                                    onChange={(e) => setHideFerryRoutes(e.target.checked)}
                                    className="rs-ferry-filter-checkbox"
                                />
                                Hide ferry routes
                            </label>
                        )}
                    </div>

                    <div className="rs-table-container">
                        <table className="rs-route-table">
                            <thead>
                                <tr>
                                    <th><span className="rs-th-icon">🛫</span> Depart</th>
                                    <th><span className="rs-th-icon">🛬</span> Arrive</th>
                                    <th><span className="rs-th-icon">🌤️</span> Weather</th>
                                    <th><span className="rs-th-icon">📏</span> Distance</th>
                                    <th><span className="rs-th-icon">⏱️</span> Time</th>
                                    <th><span className="rs-th-icon">✈️</span> Type</th>
                                    <th><span className="rs-th-icon">📍</span> Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredResults.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="rs-no-results">
                                            No routes found. Try adjusting your search criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredResults.map((row, index) => {
                                        const wxcat = row.frule;
                                        let wxClass = '';
                                        let wxTitle = '';
                                        
                                        switch(wxcat) {
                                            case "VFR": wxClass = 'rs-wx-vfr'; wxTitle = 'Visual Flight Rule'; break;
                                            case "MVFR": wxClass = 'rs-wx-mvfr'; wxTitle = 'Marginal Visual Flight Rule'; break;
                                            case "IFR": wxClass = 'rs-wx-ifr'; wxTitle = 'Instrument Flight Rule'; break;
                                            case "LIFR": wxClass = 'rs-wx-lifr'; wxTitle = 'Low Instrument Flight Rule'; break;
                                            default: wxClass = ''; wxTitle = ''; break;
                                        }
                                        
                                        const isHighlighted = Math.random() < 0.15;

                                        return (
                                            <tr key={index} className={isHighlighted ? 'rs-highlighted-route' : ''}> 
                                                <td className="rs-airport-code">{row.orig}</td> 
                                                <td className="rs-airport-code">{row.dest}</td> 
                                                <td title={wxTitle}>
                                                    <span className={`rs-wx-badge ${wxClass}`}>{wxcat}</span>
                                                </td> 
                                                <td>{row.nm} NM</td> 
                                                <td>{row.eta}</td> 
                                                <td><span className="rs-type-badge">{row.type}</span></td>
                                                <td>
                                                    <button 
                                                        className="rs-btn-select" 
                                                        onClick={() => handleRouteSelect(row)}
                                                        title={`Select route ${row.orig}-${row.dest}`}
                                                    >
                                                        Select
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                    );
                })()}
            </div>
        );
    };

    const renderFlightDetails = () => {
        if (!routeDetails) return null;

        const { route: r, map_data: m } = routeDetails;
        const displayFplan = r.fplan ?? selectedRoute?.fplan ?? null;
        const displayAlternate = r.alternate ?? selectedRoute?.alternate ?? null;
        const hasSupplementalPlanInfo = [displayFplan, displayAlternate].some(
            (value) => value !== null && value !== undefined && String(value).trim() !== ''
        );
        const hasMapData =
            m &&
            [m.olat, m.olon, m.dlat, m.dlon].every((value) =>
                Number.isFinite(Number(value))
            );

        return (
            <div className="rs-container">
                {/* Action Bar */}
                <div className="rs-action-bar">
                    <button className="rs-btn-back" onClick={handleBackToResults}>
                        <span className="rs-btn-icon">←</span>
                        Back to Results
                    </button>
                </div>

                {/* Jumpseat Notice */}
                {r.orig && currentPilotLocation && r.orig !== currentPilotLocation && (
                    <div className="rs-jumpseat-notice">
                        <span className="rs-jumpseat-notice-icon">⚠️</span>
                        <div className="rs-jumpseat-notice-body">
                            <span className="rs-jumpseat-notice-title">Jumpseat Required</span>
                            <p className="rs-jumpseat-notice-text">
                                The departure airport <strong>{r.orig}</strong> does not match your current location (<strong>{currentPilotLocation}</strong>). You will need to arrange a jumpseat to <strong>{r.orig}</strong> before operating this flight.
                            </p>
                        </div>
                    </div>
                )}

                {/* Flight Details - Single Column */}
                <div className="rs-detail-card">
                    <div className="rs-card-header">
                        <div className="rs-card-icon"></div>
                        <h2 className="rs-card-title">Flight Information</h2>
                        <button className="rs-btn-primary rs-generate-loadsheet" onClick={() => setShowLoadsheetModal(true)}>
                            <span className="rs-btn-icon">📄</span>
                            Generate Loadsheet
                        </button>
                    </div>
                    
                    <div className="rs-detail-content rs-two-columns">
                        <div className="rs-detail-column">
                            <div className="rs-detail-row">
                                <div className="rs-detail-label">
                                    <span className="rs-detail-icon">🛫</span>
                                    Departure
                                </div>
                                <div className="rs-detail-value">
                                    <strong>{r.orig}</strong> - {r.oapt}
                                </div>
                            </div>

                            <div className="rs-detail-row">
                                <div className="rs-detail-label">
                                    <span className="rs-detail-icon">🛬</span>
                                    Arrival
                                </div>
                                <div className="rs-detail-value">
                                    <strong>{r.dest}</strong> - {r.dapt}
                                </div>
                            </div>
                        </div>

                        <div className="rs-detail-column">
                            <div className="rs-detail-row rs-distance-column">
                                <div className="rs-detail-value">
                                    {r.nm} NM
                                </div>
                            </div>
                            <div className="rs-detail-row rs-time-column">
                                <div className="rs-detail-value">
                                    ETE {r.ete} hours
                                </div>
                            </div>
                            {/* Right column content */}
                        </div>

                        {/* Message row that spans both columns */}
                        <div className="rs-detail-row rs-message-row">
                            <div> Total flights: {r.total_flights} | Average Time: {r.avg_time} | Last flown: {r.last_flown}    </div> 

                        </div>

                        {hasSupplementalPlanInfo && (
                            <div className="rs-detail-row rs-message-row rs-supplemental-plan-row">
                                {displayFplan && (
                                    <div className="rs-supplemental-plan-item">
                                        <div className="rs-plan-header">
                                            <span className="rs-detail-icon">🗺️</span>
                                            Flight Plan 
                                        </div>
                                        <div>{displayFplan}</div>
                                    </div>
                                )}

                                {displayAlternate && (
                                    <div className="rs-supplemental-plan-item">
                                        <div className="rs-plan-header">
                                            <span className="rs-detail-icon">🛬</span>
                                            FDXVA Alternate
                                        </div>
                                        <div><strong>{displayAlternate}</strong></div>
                                    </div>
                                )}
                            </div>
                        )}


                        {/* Flight Plan row – always visible, with Edit button */}
                        <div className="rs-detail-row rs-message-row">
                            <div className="rs-plan-header">
                                <span className="rs-detail-icon">✈️</span>
                                My Notes / Plan
                                <button
                                    className="rs-btn-edit-plan"
                                    onClick={() => {
                                        setEditPlan(r.plan || '');
                                        setShowPlanModal(true);
                                    }}
                                    title="Edit flight plan"
                                >
                                    ✏️ Edit
                                </button>
                            </div>
                            <div>{r.plan || <em className="rs-plan-empty">No flight plan on file.</em>}</div>
                        </div>
                        {/* Message row that spans both columns */}
                        <div className="rs-detail-row-plans">
                            <div className="rs-btn-primary" onClick={()=>window.open('https://my.vatsim.net/pilots/flightplan','_blank')}>Vatsim</div> 

                            {/* <SimbriefButton origin={r.orig} destination={r.dest} aircraftType={selectedAircraft} /> */}
<SimbriefButton 
    origin={r?.orig ?? ''} 
    destination={r?.dest ?? ''} 
    aircraftType={selectedAircraft?.trim() || 'B77F'} 
/>

<IvaoButton 
    callsign={callsign}
    origin={r?.orig ?? ''} 
    destination={r?.dest ?? ''} 
    aircraftType={selectedAircraft?.trim() || 'B77F'} 
    route = {displayFplan}
/>                      
                        </div>

                        {/* Map Section */}
                        <div className="rs-detail-map-section">
                            {/* <div className="rs-map-section-header">
                                <div className="rs-card-icon"></div>
                                <h3 className="rs-map-section-title"></h3>
                            </div> */}
                            <div className="rs-map-wrapper">
                                {hasMapData ? (
                                    <FlightMap
                                        orig={{ lat: Number(m.olat), lon: Number(m.olon) }}
                                        dest={{ lat: Number(m.dlat), lon: Number(m.dlon) }}
                                        color={m.color}
                                        originIcao={r.orig}
                                        destinationIcao={r.dest}
                                    />
                                ) : (
                                    <div className="rs-error-message">Map data unavailable for this route.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit Flight Plan Modal */}
                {showPlanModal && (
                    <div className="rs-modal-overlay" onClick={() => setShowPlanModal(false)}>
                        <div className="rs-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="rs-modal-header">
                                <h2 className="rs-modal-title">
                                    <span className="rs-modal-icon">✈️</span>
                                    Edit Flight Plan
                                </h2>
                                <button
                                    className="rs-modal-close"
                                    onClick={() => setShowPlanModal(false)}
                                    aria-label="Close modal"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="rs-modal-form">
                                <div className="rs-form-group">
                                    <label htmlFor="plan-edit">
                                        <span className="rs-label-icon">🗺️</span>
                                        Flight Plan Route — {r.orig} → {r.dest}
                                    </label>
                                    <textarea
                                        id="plan-edit"
                                        className="rs-textarea-field"
                                        value={editPlan}
                                        onChange={(e) => setEditPlan(e.target.value.toUpperCase())}
                                        rows={5}
                                        placeholder="Enter waypoints / airway route string..."
                                        autoFocus
                                    />
                                </div>

                                <div className="rs-modal-actions">
                                    <button
                                        type="button"
                                        className="rs-btn-secondary"
                                        onClick={() => setShowPlanModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="rs-btn-primary"
                                        onClick={() => {
                                            // Optimistic update
                                            setRouteDetails(prev => ({
                                                ...prev,
                                                route: { ...prev.route, plan: editPlan }
                                            }));
                                            setShowPlanModal(false);
                                            // Persist to server
                                            updateFlightPlan(r.orig, r.dest, editPlan);
                                        }}
                                    >
                                        <span className="rs-btn-icon">💾</span>
                                        Save Plan
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loadsheet Modal */}
                {showLoadsheetModal && (
                    <div className="rs-modal-overlay" onClick={() => setShowLoadsheetModal(false)}>
                        <div className="rs-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="rs-modal-header">
                                <h2 className="rs-modal-title">
                                    <span className="rs-modal-icon">📄</span>
                                    Generate Loadsheet
                                </h2>
                                <button 
                                    className="rs-modal-close" 
                                    onClick={() => setShowLoadsheetModal(false)}
                                    aria-label="Close modal"
                                >
                                    ✕
                                </button>
                            </div>

                            <form onSubmit={handleLoadsheetSubmit} className="rs-modal-form">
                                <div className="rs-form-group">
                                    <label htmlFor="aircraft">
                                        <span className="rs-label-icon">✈️</span>
                                        Select Aircraft
                                    </label>
                                    <select
                                        id="aircraft"
                                        value={selectedAircraft}
                                        onChange={(e) => {
                                            setSelectedAircraft(e.target.value);
                                            localStorage.setItem('selectedAircraft', e.target.value);
                                        }}
                                        className="rs-select-field"
                                        required
                                        size="5"
                                    >
                                        <option value="">Choose an aircraft...</option>
                                        {aircraftOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.value} - {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="rs-form-options">
                                    <label className="rs-checkbox-label">
                                        <input 
                                            type="checkbox" 
                                            checked={useKG}
                                            onChange={(e) => {
                                                setUseKG(e.target.checked);
                                                localStorage.setItem('useKG', e.target.checked);
                                            }}
                                        />
                                        <span className="rs-checkbox-text">Use Kilograms (KG)</span>
                                    </label>

                                    {/* {r.is_jumpseat_available && (
                                        <label className="rs-checkbox-label">
                                            <input 
                                                type="checkbox" 
                                                checked={useJumpseat}
                                                onChange={(e) => setUseJumpseat(e.target.checked)}
                                            />
                                            <span className="rs-checkbox-text">Jumpseat to {r.orig}</span>
                                        </label>
                                    )} */}

                                </div>

                                <div className="rs-modal-actions">
                                    <button 
                                        type="button" 
                                        className="rs-btn-secondary"
                                        onClick={() => setShowLoadsheetModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="rs-btn-primary">
                                        <span className="rs-btn-icon">📄</span>
                                        Generate Loadsheet
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    let content;
    if (loading) {
        content = (
            <div className="rs-loading-container">
                <div className="rs-loading-spinner"></div>
                <p className="rs-loading-text">Searching for routes...</p>
            </div>
        );
    } else if (error) {
        content = (
            <div className="rs-error-container">
                <div className="rs-error-icon">⚠️</div>
                <h2 className="rs-error-title">Error</h2>
                <p className="rs-error-message">{error}</p>
                <button className="rs-btn-primary" onClick={() => { setError(null); setStage(0); }}>
                    Return to Search
                </button>
            </div>
        );
    } else if (stage === 1) {
        content = renderResultsTable();
    } else if (stage === 2) {
        content = renderFlightDetails();
    } else {
        content = renderSearchForm();
    }

    return (
        <div className={`rs-page ${props.darkmode ? 'dark-theme' : 'light-theme'}`}>
            {content}
            
        </div>
    );
};

export default RouteSearch;
